import { useEffect, useMemo, useCallback, useRef, useState } from "react";
import { KalturaESearchHistory } from "kaltura-typescript-client/api/types/KalturaESearchHistory";
import { KalturaESearchHistoryListResponse } from "kaltura-typescript-client/api/types/KalturaESearchHistoryListResponse";
import { CancelableAction, KalturaClient } from "kaltura-typescript-client";
import { SearchHistoryListAction } from "kaltura-typescript-client/api/types/SearchHistoryListAction";
import { KalturaESearchHistoryFilter } from "kaltura-typescript-client/api/types/KalturaESearchHistoryFilter";
import { SearchHistoryDeleteAction } from "kaltura-typescript-client/api/types/SearchHistoryDeleteAction";
import { useConfig } from "@kaltura/mediaspace-shared-data-kms-config";

/**
 * get search suggestions from the API
 */
export const useSearchSuggestions = (value: string) => {
    const [suggestions, setSuggestions] = useState<KalturaESearchHistory[]>();
    const lastSearchActionRef = useRef<CancelableAction<KalturaESearchHistoryListResponse>>();
    const config = useConfig();

    // init client
    const client = useMemo(() => {
        if (!config.globalSearch?.client) {
            return null;
        }
        return new KalturaClient(
            {
                endpointUrl: config.globalSearch.client.serviceUrl,
                clientTag: config.globalSearch.client.clientTag,
            },
            {
                ks: config.globalSearch.client.ks,
            }
        );
    }, [config]);

    // remove search suggestion from the list
    const removeTerm = useCallback(
        (term: string) => {
            const request = new SearchHistoryDeleteAction({
                searchTerm: term,
            });
            client?.request(request).then(
                () => {
                    setSuggestions((prevSuggestions) => prevSuggestions?.filter((sug) => sug.searchTerm !== term));
                },
                (err) => {
                    console.error("error deleting search history: ", err);
                }
            );
        },
        [client]
    );

    // get search suggestions
    useEffect(() => {
        const getRecentSearches = (term: string) => {
            if (!client) {
                return;
            }
            if (term.length < 3) {
                setSuggestions(undefined);
                return;
            }

            const request = new SearchHistoryListAction();
            request.filter = new KalturaESearchHistoryFilter();
            request.filter.searchTermStartsWith = term;
            request.filter.searchedObjectIn = "entry";

            const kESearchHistoryListResponse = client.request(request).then(
                (data) => {
                    lastSearchActionRef.current = undefined;
                    if (data && data.objects.length) {
                        setSuggestions(data.objects);
                    }
                    else {
                        setSuggestions(undefined);
                    }
                },
                (err) => {
                    setSuggestions(undefined);
                    lastSearchActionRef.current = undefined;
                    console.error("error getting search history: ", err);
                }
            );
            lastSearchActionRef.current = kESearchHistoryListResponse;
        };

        if (lastSearchActionRef.current) {
            lastSearchActionRef.current.cancel();
        }

        getRecentSearches(value);

        return () => {
            if (lastSearchActionRef.current) {
                lastSearchActionRef.current.cancel();
            }
        };
    }, [value, client]);

    return {
        suggestions,
        removeTerm,
    };
};

export default useSearchSuggestions;
